
import {
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  reactive,
  Ref,
  ref,
  toRefs,
} from "vue";
import { useI18n } from "vue-i18n";
import { DocumentNode } from "graphql";
import { Icon } from "@iconify/vue";
import { OnClickOutside } from "@vueuse/components";

import usePaginatedQuery from "@/composables/usePaginatedQuery";
import { OrderTimesheetReport } from "@/helpers/invoice/typesDefinition";
import { formatPrice } from "@/helpers/utils";

import config from "@/config";

import DropdownMenu from "@/components/dropDownMenu/DropDownMenu.vue";
import DropDownItem from "@/components/dropDownMenu/DropDownItem.vue";
import IconButton from "@/components/IconButton.vue";

export default defineComponent({
  props: {
    query: {
      type: Object as PropType<DocumentNode>,
      required: true,
    },

    queryParams: {
      type: Object,
      default: () => ({}),
    },

    items: {
      type: Array,
      required: false,
    },
  },

  setup(props, context) {
    const { t } = useI18n({
      useScope: "global",
      inheritLocale: true,
    });

    const propsRef = toRefs(props);
    const queryRef = propsRef.query;
    const queryParamsRef = propsRef.queryParams;

    const endOfList = ref() as Ref<HTMLElement>;

    const refresh = () => refetch();

    context.expose({
      refresh,
    });

    const { paginatedResult, loading, loadMore, onResult, refetch } =
      usePaginatedQuery(queryRef, t("errors.list"), queryParamsRef);

    /*
     * Client expansion.
     */
    const expandedClientIds = ref<Set<string>>(new Set());

    const toggleClient = (clientId: string) => {
      const updated = new Set(expandedClientIds.value);

      if (updated.has(clientId)) {
        updated.delete(clientId);
      } else {
        updated.add(clientId);
      }

      expandedClientIds.value = updated;
    };

    const isClientExpanded = (clientId: string): boolean =>
      expandedClientIds.value.has(clientId);

    /*
     * Draft-timesheets dropdown.
     *
     * Same behaviour as EntitiesList:
     * only one dropdown can be opened at once.
     */
    const activeDropdown = ref("");

    const isDropdownActive = (projectId: string): boolean =>
      activeDropdown.value === projectId;

    const toggleDropdown = (projectId: string) => {
      activeDropdown.value =
        activeDropdown.value === projectId ? "" : projectId;
    };

    const closeDropdown = () => {
      activeDropdown.value = "";
    };

    /*
     * DropdownMenu needs the corresponding DOM element
     * as its positioning reference.
     */
    const targetElements = reactive<Record<string, HTMLElement | null>>({});

    const getRef = (element: HTMLElement | null, projectId: string) => {
      targetElements[projectId] = element;
    };

    const backendUri = config.backendUri;

    /*
     * Keep v-model:items working so ProjectsToInvoice can
     * calculate its KPIs from the loaded results.
     */
    if (paginatedResult.value) {
      context.emit("update:items", paginatedResult.value.items);
    }

    onMounted(() => {
      const handler = (entries: IntersectionObserverEntry[]) => {
        if (entries[0]?.isIntersecting && paginatedResult?.value?.hasMore) {
          loadMore();
        }
      };

      const observer = new window.IntersectionObserver(handler);

      if (endOfList.value) {
        observer.observe(endOfList.value);
      }
    });

    onResult(async () => {
      context.emit("update:items", paginatedResult.value?.items ?? []);

      await nextTick();

      if (!endOfList.value) return;

      const rect = endOfList.value.getBoundingClientRect();

      if (
        rect.bottom <= document.documentElement.clientHeight &&
        paginatedResult?.value?.hasMore &&
        !loading.value
      ) {
        loadMore();
      }
    });

    return {
      t,

      paginatedResult,
      loading,
      endOfList,

      formatPrice,

      toggleClient,
      isClientExpanded,

      backendUri,
      activeDropdown,
      isDropdownActive,
      toggleDropdown,
      closeDropdown,
      targetElements,
      getRef,
    };
  },

  emits: ["update:items", "actionOnClick", "showComments"],

  components: {
    Icon,
    IconButton,
    DropdownMenu,
    DropDownItem,
    OnClickOutside,
  },

  methods: {
    emitActionOnClick(project: OrderTimesheetReport) {
      this.$emit("actionOnClick", project);
    },

    emitShowComments(clientId: string, clientName: string, event: Event) {
      event.stopPropagation();
      this.$emit("showComments", { clientId, clientName });
    },

    getClientStatus(projects: OrderTimesheetReport[]): "draft" | "new" {
      return projects.some((project) => !!project.draftInvoice)
        ? "draft"
        : "new";
    },

    hasOfferedAmountOnProject(project: OrderTimesheetReport): boolean {
      return project.hasUnvalidatedOfferedAmount;
    },
  },
});
